import React, { Component } from "react"
import { DropdownContainer } from "buoy-dropdown"
import { HeadingTwo, HeadingThree, Paragraph } from "buoy-text"
import { colors } from "buoy-theme"
import RecommendationCard from "../../common/components/RecommendationCard"
import ScrollTracker from "../../interview/components/ScrollTracker"
import ZipcodeInput from "./ZipcodeInput"
import AccountSource from "../../account/Source"
import { postUserInsurancePlan, updateUserInsurancePlan } from "../../account/actions"
import { getPlanInformation } from "../../dashboard/actions"
import * as integrationActions from "../../integration/actions"
import { STATIC_URL } from "../../common/constants"
import styled from "styled-components"
import { connect } from "react-redux"
import { FormattedMessage, injectIntl, defineMessages } from "react-intl"

const messages = defineMessages({
  nextStepsSelectInsurance: {
    id: "nextsteps.select.insurance",
    defaultMessage: "Select Insurance Carrier"
  }
})

const StyledMainArea = styled.div``

const StyledHeadingArea = styled.div`
  ${HeadingTwo} {
    margin-bottom: 16px;
  }
`

const StyledSummaryArea = styled.div`
  & > p:not(:last-child) {
    margin-bottom: 12px;
  }
`

const StyledInputArea = styled.div`
  margin-top: 16px;
  & > *:not(:last-child) {
    margin-bottom: 12px;
  }
`

const StyledRecommendationSection = styled.div`
  border-top: solid 1px ${colors.neutralLighter};
  margin-top: 24px;
`

const StyledInsuranceMessage = styled.div`
  ${HeadingThree} {
    margin-bottom: 0;
  }
`

const StyledUpdatedTime = styled(Paragraph)`
  color: ${colors.neutral};
  font-style: italic;
  margin-top: 0;
`

const StyledSummary = styled(Paragraph)`
  margin-top: 12px;
`

const InsuranceMessage = ({ message, title, updatedTime }) => {
  const formatTime = () => {
    const date = new Date(updatedTime)
    let hourOfDay = date.getHours() % 12
    if (hourOfDay === 0) hourOfDay = 12
    const month = date.getMonth() + 1
    const dayTime = hourOfDay > 11 ? "PM" : "AM"
    const minutes = String(date.getMinutes()).padStart(2, 0)

    return `Last Updated ${month}/${date.getDate()} @ ${hourOfDay}:${minutes} ${dayTime}`
  }

  return (
    <StyledInsuranceMessage>
      <HeadingThree>{title}</HeadingThree>
      <StyledUpdatedTime>{formatTime()}</StyledUpdatedTime>
      <StyledSummary>{message}</StyledSummary>
    </StyledInsuranceMessage>
  )
}

const OTHER_INSURANCE_DISCLAIMER = "Note: Since you selected “Other”, the service below may not be covered by your insurance. Please contact your insurance carrier to confirm coverage."
const REQUIRED_INPUT_TEXT_ZIP_ONLY = "Enter your zip code to see what’s available in your area."
const REQUIRED_INPUT_TEXT_ALL = "Enter your insurance and zip code to see what’s available in your area."
const TEST_CENTER_AVAILABILITY_TEXT = "Information on test locations will appear below if available."

const Summary = triage => {
  switch (triage) {
    case 2:
    case 5:
      return (
        <Paragraph>
          <FormattedMessage
            id="nextsteps.body"
            defaultMessage="If you have a primary care doctor, contact them by phone. If they're unavailable, telemedicine may also be an option based on your coverage."
          />
        </Paragraph>
      )
    case 6:
      return (
        <Paragraph>
          <FormattedMessage
            id="nextsteps.body.6"
            defaultMessage="Enter your details to see the nearest emergency room. Note: We’re still working to gather information for certain areas. If results don't appear, please visit your local emergency room directly."
          />
        </Paragraph>
      )
    default:
      return null
  }
}

class AlarmRecommendationSection extends Component {
  constructor() {
    super()
    this.state = {
      canDisplayRecommendations: false,
      shouldShowDropDown: false,
      shouldShowOtherDisclaimer: false,
      plans: [],
    }
  }

  componentDidMount() {
    this.getPlans()
    this.setOtherInsuranceDisclaimer()
    const canDisplayRecommendations = this.setDisplayConditions()
    if (this.props.didCaptureLocation) this.props.getInsuranceMessages()
    if (canDisplayRecommendations) this.props.updateVisitRecommendations()
    if (this.props.canUpdateInsurance && !this.props.planInformation.plan) this.props.getPlanInformation()
  }

  componentDidUpdate(prevProps) {
    let { canDisplayRecommendations } = this.state
    const planChanged = prevProps.planInformation.plan !== this.props.planInformation.plan
    const locationCaptured = prevProps.didCaptureLocation !== this.props.didCaptureLocation
    if (prevProps.marketplaceToken !== this.props.marketplaceToken) this.getPlans()
    if (planChanged) this.setOtherInsuranceDisclaimer()
    if (planChanged || locationCaptured) canDisplayRecommendations = this.setDisplayConditions()
    // if the location changes the locationSubmit will automatically retrieve new visit recommendations
    // fetching here on a location change would result in 2 calls to get recommendations
    if (planChanged && canDisplayRecommendations) {
      this.props.updateVisitRecommendations()
    }
  }

  setDisplayConditions() {
    // we only want to show recommendations if we have the user's location
    // and if we allow updating insurance, the user has provided their insurance info,
    // or we do not have plans for the marketplace
    const canDisplayRecommendations =  this.props.didCaptureLocation && (this.props.planInformation.plan || !this.props.canUpdateInsurance || !this.state.plans.length > 0)

    this.setState({ canDisplayRecommendations })

    return canDisplayRecommendations
  }

  getPlans = () => {
    if (this.props.marketplaceToken) {
      AccountSource.getMarketplaceInsurancePlans(this.props.marketplaceToken).then(data => {
        const plans = data.insurance_plans.map(plan => {
          return { text: plan.provider.name, value: plan.token }
        })
        this.setState({ plans })
      })
    }
  }

  selectPlan = selection => {
    const plan = selection.value
    if (this.props.planInformation.token) {
      this.props.updateUserInsurancePlan("", plan, "SE")
    } else {
      this.props.postUserInsurancePlan("", plan, "SE")
    }
    this.setState({ shouldShowDropDown: false })
  }

  onDropDownFocus = value => {
    this.setState({ shouldShowDropDown: true })
  }

  setOtherInsuranceDisclaimer = () => {
    let shouldShowOtherDisclaimer = false
    if (this.props.planInformation.plan) {
      const plan = this.state.plans.find(
        plan => plan.value === this.props.planInformation.plan
      )
      if (plan && plan.text === "Other") {
        shouldShowOtherDisclaimer = true
      }
    }

    this.setState({ shouldShowOtherDisclaimer })

    return shouldShowOtherDisclaimer
  }

  render() {
    return (
      <StyledMainArea>
        <StyledHeadingArea>
          <HeadingTwo>
            <FormattedMessage
              id="nextsteps.header"
              defaultMessage="Next steps for care"
            />
          </HeadingTwo>
          <StyledSummaryArea>
            {Summary(this.props.results.triage.level)}
            {this.props.canUpdateInsurance && this.state.plans.length > 0 ? (
              <Paragraph>
                <FormattedMessage
                  id="nextsteps.required.input.text.all"
                  defaultMessage={REQUIRED_INPUT_TEXT_ALL}
                />
                </Paragraph>
            ) : (
              <Paragraph>
                <FormattedMessage
                  id="nextsteps.required.input.text.zip"
                  defaultMessage={REQUIRED_INPUT_TEXT_ZIP_ONLY}
                />
                </Paragraph>
            )}
            {this.props.results.triage.level === 2 && (
              <Paragraph>
                <FormattedMessage
                  id="nextsteps.test.center.availability"
                  defaultMessage={TEST_CENTER_AVAILABILITY_TEXT}
                />
              </Paragraph>
            )}
          </StyledSummaryArea>
          <StyledInputArea>
            {this.props.canUpdateInsurance && this.state.plans.length > 0 && (
              <DropdownContainer
                options={this.state.plans}
                required={false}
                value={this.props.planInformation.plan}
                placeholder={this.props.intl.formatMessage(
                  messages.nextStepsSelectInsurance
                )}
                onChange={this.selectPlan}
                onFocus={this.onDropDownFocus}
                focused={this.state.shouldShowDropDown}
              />
            )}
            <ZipcodeInput autoFocus={false} />
          </StyledInputArea>
        </StyledHeadingArea>
        {this.state.canDisplayRecommendations &&
          Object.keys(this.props.recommendationSections).indexOf("alarm") !==
            -1 && (
            <StyledRecommendationSection>
              {this.props.insuranceMessages.map(message => {
                return (
                  <InsuranceMessage
                    message={message.message}
                    title={message.title}
                    updatedTime={message.modified * 1000}
                  />
                )
              })}
              {this.state.shouldShowOtherDisclaimer && (
                <StyledSummary>{OTHER_INSURANCE_DISCLAIMER}</StyledSummary>
              )}
              {this.props.recommendationSections["alarm"].map((rec, i) => (
                <ScrollTracker
                  key={rec.token}
                  sectionTitle={`Resource - alarm ${i + 1}`}
                >
                  <RecommendationCard
                    token={rec.token}
                    key={rec.recommendation_token}
                    ctaText={rec.ctaText}
                    ctaUrl={rec.ctaUrl}
                    highlights={rec.highlights}
                    infoList={rec.info}
                    tags={rec.tags}
                    headline={rec.headline}
                    description={rec.summary}
                    image={rec.image ? `${STATIC_URL}${rec.image}` : null}
                    requirePayment={rec.requirePayment}
                    requireProfile={rec.requireProfile}
                    diffToken={"alarm"}
                    initPayment={this.props.initPayment}
                    initIntellivisit={this.props.initIntellivisit}
                    rec={rec}
                    fullBorder
                  />
                </ScrollTracker>
              ))}
            </StyledRecommendationSection>
          )}
      </StyledMainArea>
    )
  }
}

AlarmRecommendationSection.defaultProps = {
  initIntellivisit: null,
  initPayment: null,
  marketplaceToken: null,
  planInformation: {},
}

const mapStateToProps = state => ({
  canUpdateInsurance: state.integration.canUpdateInsurance,
  didCaptureLocation: state.integration.didCaptureLocation,
  insuranceMessages: state.integration.insuranceMessages,
  marketplaceToken: state.brand.token,
  planInformation: state.dashboard.planInformation,
  recommendationSections: state.integration.recommendationSections,
  results: state.completion.results,
  zipcode: state.integration.zipcode,
})

const mapDispatchToProps = dispatch => ({
  getInsuranceMessages: () => dispatch(integrationActions.getRegionBasedInsuranceMessages()),
  getPlanInformation: () => dispatch(getPlanInformation()),
  postUserInsurancePlan: (office, plan, planHolder) => dispatch(postUserInsurancePlan(office, plan, planHolder)),
  updateUserInsurancePlan: (office, plan, planHolder) => dispatch(updateUserInsurancePlan(office, plan, planHolder)),
  updateVisitRecommendations: () => dispatch(integrationActions.updateVisitRecommendations())
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(AlarmRecommendationSection))



// WEBPACK FOOTER //
// ./assets/completion/components/AlarmRecommendationSection.js